import type { RecordsTableConfig } from '@cims-online/shared'
import { useTableFilter } from 'hooks/useTableFilter'
import { useMemo } from 'react'
import type { WorkflowRecord } from '../../types'
import { toColumn } from './columns'
import { expandOptions } from './expand'
import type { RecordsTableActionContext, RecordsTableOptions } from './types'

/**
 * Adapts the server-owned records-table configuration into stable native Ant
 * Design table options.
 *
 * This hook only coordinates the translation. Column behavior and expandable
 * content are implemented in their focused modules, while all field selection,
 * order, widths, help text, and defaults remain owned by the server.
 */
export function useRecordsTable(
  config: RecordsTableConfig,
  records: WorkflowRecord[],
  { canCreate, controller, onCommentAdded }: RecordsTableActionContext
): RecordsTableOptions {
  const tableFilter = useTableFilter(records)

  return useMemo(() => {
    const columns = config.columns.map((column) =>
      toColumn(column, tableFilter, controller)
    )
    return {
      columns,
      expandable: expandOptions(config.expand, {
        canCreate,
        onCommentAdded,
      }),
    }
  }, [canCreate, config, controller, onCommentAdded, tableFilter])
}
